import { ObjectConfig, useFormState } from "@homebound/form-state";
import { useCallback } from "react";
import { SaveCostCodeInput } from "src/generated/graphqlTypes";
import { useCostCodeMaterialLeadTimesTabQuery } from "src/routes/admin/costCodes/CostCodeMaterialLeadTimesTab.generated";
import { useSaveCostCodesMutation } from "src/routes/admin/costCodes/CostCodeOverviewTab.generated";
import { BoundMaterialLeadTimeConfigFields } from "../components/BoundMaterialLeadTimeConfigFields";
import {
  MaterialLeadTimeFormValue,
  initializeMaterialLeadTimeConfigFields,
  materialLeadTimeConfigConfig,
  maybeMaterialLeadTimeConfig,
} from "../utils";

type CostCodeOverviewTabProps = {
  costCodeId: string;
};

export function CostCodeMaterialLeadTimesTab({ costCodeId }: CostCodeOverviewTabProps) {
  const query = useCostCodeMaterialLeadTimesTabQuery({ variables: { filter: { id: [costCodeId], version: [1, 2] } } });
  const [saveCostCodes] = useSaveCostCodesMutation();
  const costCode = query.data?.costCodes[0];

  const onDelete = useCallback(async () => {
    // we can only delete a "config" if it actually exists
    if (costCode?.materialLeadTimeConfig?.id) {
      await saveCostCodes({
        variables: {
          input: {
            costCodes: [
              { id: costCode.id, materialLeadTimeConfig: { delete: true, id: costCode.materialLeadTimeConfig.id } },
            ],
          },
        },
      });
    }
  }, [saveCostCodes, costCode]);

  const formState = useFormState({
    config: formConfig,
    init: {
      input: costCode,
      map: (costCode) => ({
        materialLeadTimeConfig: {
          ...initializeMaterialLeadTimeConfigFields(costCode.materialLeadTimeConfig),
        },
      }),
    },
    autoSave: async ({ changedValue }) => {
      const { materialLeadTimeConfig } = changedValue;
      if (formState.canSave()) {
        await saveCostCodes({
          variables: {
            input: {
              costCodes: [
                {
                  id: costCode?.id,
                  ...maybeMaterialLeadTimeConfig({
                    id: costCode?.materialLeadTimeConfig?.id,
                    ...materialLeadTimeConfig,
                  }),
                },
              ],
            },
          },
        });
      }
    },
  });

  return (
    <BoundMaterialLeadTimeConfigFields
      minLeadTimeInDays={formState.materialLeadTimeConfig.minLeadTimeInDays}
      expectedLeadTimeInDays={formState.materialLeadTimeConfig.expectedLeadTimeInDays}
      maxLeadTimeInDays={formState.materialLeadTimeConfig.maxLeadTimeInDays}
      reasonNote={formState.materialLeadTimeConfig.reasonNote}
      deleteConfig={{ canDelete: !!costCode?.materialLeadTimeConfig?.id, onDelete }}
    />
  );
}

type FormValue = SaveCostCodeInput & {
  materialLeadTimeConfig: MaterialLeadTimeFormValue;
};

const formConfig: ObjectConfig<FormValue> = {
  materialLeadTimeConfig: { type: "object", config: materialLeadTimeConfigConfig },
};
